import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Context } from 'src/app/context';
import { PlanLevel, PlanModel, SubscriptionStatus } from 'src/app/models/plan.model';
import { PlanService } from 'src/app/services/plan.service';

@Component({
  selector: 'app-playback-eligibility',
  templateUrl: './playback-eligibility.component.html',
  styleUrls: ['./playback-eligibility.component.scss'],
})
export class PlaybackEligibilityComponent {
  notEligible = false;
  hasUpgradePlans = true;
  subscriptionRequired = false;

  private readonly levelRank: Record<PlanLevel, number> = {
    [PlanLevel.Free]: 0,
    [PlanLevel.Basic]: 1,
    [PlanLevel.Premium]: 2
  };

  constructor(
    private planService: PlanService,
    private router: Router) {
  }

  async canPlay(): Promise<boolean> {
    try {
      const canPlay = await this.planService.canPlaySong();
      if (canPlay) {
        this.notEligible = false;
        this.subscriptionRequired = false;
        return true;
      }
      if (Context.user?.subscriptionStatus === SubscriptionStatus.Paused) {
        await this.router.navigateByUrl('/manage-plan');
        return false;
      }

      if (Context.user?.subscriptionStatus === SubscriptionStatus.Cancelled) {
        this.subscriptionRequired = true;
        this.notEligible = true;
        return false;
      }

      this.subscriptionRequired = false;
      this.notEligible = true;
      await this.loadUpgradeAvailability();
      return false;
    } catch {
      this.notEligible = false;
      return true;
    }
  }

  goToUpgrade() {
    this.router.navigateByUrl('/upgrade');
  }

  subscribe() {
    this.router.navigateByUrl('/login');
  }

  goHome() {
    this.router.navigateByUrl('/');
  }

  private async loadUpgradeAvailability() {
    try {
      const [plans, currentPlan] = await Promise.all([
        this.planService.getUpgradePlans(),
        this.planService.getCurrentPlan()
      ]);
      if (!currentPlan) {
        this.hasUpgradePlans = false;
        return;
      }

      this.hasUpgradePlans = plans.some(p => this.isActualUpgrade(currentPlan, p));
    } catch {
      this.hasUpgradePlans = true;
    }
  }

  private isActualUpgrade(current: PlanModel, candidate: PlanModel): boolean {
    if (this.levelRank[candidate.level] > this.levelRank[current.level]) return true;

    const currentMax = Math.max(0, ...current.pricePoints.map(p => p.amount));
    const candidateMax = Math.max(0, ...candidate.pricePoints.map(p => p.amount));
    return candidateMax > currentMax;
  }
}
